/**
 * Calculation Utilities
 * Core financial calculations for the mortgage guide
 */

/**
 * Calculate maximum monthly mortgage payment based on 30% rule
 * @param {number} monthlyIncome - Net monthly income
 * @returns {number} Maximum monthly payment
 */
export function calculateMaxMortgagePayment(monthlyIncome) {
    return monthlyIncome * 0.30;
}

/**
 * Calculate required down payment (20% of property price)
 * @param {number} propertyPrice - Property price
 * @returns {number} Required down payment
 */
export function calculateDownPayment(propertyPrice) {
    return propertyPrice * 0.20;
}

/**
 * Calculate additional expenses (10-12% of property price)
 * @param {number} propertyPrice - Property price
 * @param {number} percentage - Percentage (default 0.10)
 * @returns {number} Additional expenses
 */
export function calculateExpenses(propertyPrice, percentage = 0.10) {
    return propertyPrice * percentage;
}

/**
 * Calculate total savings needed for purchase
 * @param {number} propertyPrice - Property price
 * @returns {object} Breakdown of required savings
 */
export function calculateTotalSavingsNeeded(propertyPrice) {
    const downPayment = calculateDownPayment(propertyPrice);
    const expenses = calculateExpenses(propertyPrice, 0.12); // 12% for safety
    return {
        downPayment,
        expenses,
        total: downPayment + expenses
    };
}

/**
 * Calculate months needed to save for purchase
 * @param {number} propertyPrice - Target property price
 * @param {number} currentSavings - Current savings
 * @param {number} monthlySavings - Monthly savings capacity
 * @returns {number} Months needed
 */
export function calculateMonthsToSave(propertyPrice, currentSavings, monthlySavings) {
    const totalNeeded = calculateTotalSavingsNeeded(propertyPrice).total;
    const remaining = Math.max(0, totalNeeded - currentSavings);

    if (monthlySavings <= 0) return Infinity;
    return Math.ceil(remaining / monthlySavings);
}

/**
 * Calculate monthly savings from expenses
 * @param {object} expenses - Monthly expenses breakdown
 * @returns {object} Savings analysis
 */
export function calculateMonthlySavings(income, expenses) {
    const { rent = 0, fixed = 0, variable = 0 } = expenses;
    const totalExpenses = rent + fixed + variable;
    const savings = income - totalExpenses;
    const savingsPercentage = income > 0 ? (savings / income) * 100 : 0;

    return {
        totalExpenses,
        monthlySavings: savings,
        savingsPercentage,
        annualSavings: savings * 12
    };
}

/**
 * Calculate debt-to-income ratio
 * @param {number} monthlyDebt - Total monthly debt payments
 * @param {number} monthlyIncome - Net monthly income
 * @returns {number} DTI ratio as percentage
 */
export function calculateDTI(monthlyDebt, monthlyIncome) {
    if (monthlyIncome <= 0) return 0;
    return (monthlyDebt / monthlyIncome) * 100;
}

/**
 * Estimate maximum affordable property price
 * @param {number} monthlyIncome - Net monthly income
 * @param {number} currentSavings - Available savings
 * @param {number} years - Mortgage term in years (default 30)
 * @param {number} interestRate - Annual interest rate (default 3%)
 * @returns {object} Affordability analysis
 */
export function estimateAffordableProperty(monthlyIncome, currentSavings, years = 30, interestRate = 0.03) {
    const maxMonthlyPayment = calculateMaxMortgagePayment(monthlyIncome);
    const monthlyRate = interestRate / 12;
    const numPayments = years * 12;

    // Calculate max loan using mortgage formula
    // P = M * [(1+r)^n - 1] / [r * (1+r)^n]
    const factor = Math.pow(1 + monthlyRate, numPayments);
    const maxLoan = maxMonthlyPayment * ((factor - 1) / (monthlyRate * factor));

    // Max property = loan (80%) + savings (for 20% down + expenses)
    // But we need to account for expenses too
    // Savings = 20% property + 12% expenses = 32% property
    // So property = savings / 0.32
    const maxPropertyFromSavings = currentSavings / 0.32;

    // Property from loan: loan = 80% of property
    const maxPropertyFromLoan = maxLoan / 0.80;

    // The limiting factor is the lower of the two
    const maxProperty = Math.min(maxPropertyFromSavings, maxPropertyFromLoan);

    return {
        maxMonthlyPayment,
        maxLoanAmount: maxLoan,
        maxPropertyFromSavings,
        maxPropertyFromLoan,
        maxAffordableProperty: maxProperty,
        limitingFactor: maxPropertyFromSavings < maxPropertyFromLoan ? 'savings' : 'income'
    };
}

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount) {
    if (amount === undefined || amount === null || isNaN(amount)) {
        return '0 €';
    }
    return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

/**
 * Format percentage for display
 * @param {number} value - Value to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted percentage
 */
export function formatPercentage(value, decimals = 1) {
    return `${value.toFixed(decimals)}%`;
}

/**
 * Get health status based on savings percentage
 * @param {number} percentage - Savings percentage
 * @returns {object} Status with color and label
 */
export function getSavingsHealth(percentage) {
    if (percentage >= 20) {
        return { status: 'excellent', color: 'var(--success)', label: 'Excelente' };
    } else if (percentage >= 15) {
        return { status: 'good', color: 'var(--info)', label: 'Bueno' };
    } else if (percentage >= 10) {
        return { status: 'fair', color: 'var(--warning)', label: 'Regular' };
    } else {
        return { status: 'poor', color: 'var(--error)', label: 'Mejorable' };
    }
}
